import _ from 'lodash'
import type { CoreExportFormat } from '@wix/document-services-types'
import type { ComponentNode, InnerElementNode, InnerElementVisitor, TraverseOptions } from './types'

export function traverseInnerElements<T = void>(
    innerElements: unknown,
    componentId: string,
    visitor: InnerElementVisitor<T>,
): void {
    traverseInnerElementsRecursive(innerElements, componentId, visitor, [], 0, undefined)
}

function traverseInnerElementsRecursive<T>(
    innerElements: unknown,
    componentId: string,
    visitor: InnerElementVisitor<T>,
    parentPath: readonly string[],
    depth: number,
    parentContext: T | undefined,
): void {
    if (!_.isPlainObject(innerElements)) {
        throw new Error(`Inner elements of component "${componentId}" must be an object`)
    }

    const elements = innerElements as Record<string, InnerElementNode>

    for (const key of Object.keys(elements)) {
        const element = elements[key]
        if (!_.isPlainObject(element)) {
            throw new Error(`Inner element "${key}" in component "${componentId}" is not a valid object`)
        }

        const path = [...parentPath, key]

        const result = visitor({
            element,
            key,
            path,
            depth,
            componentId,
            parentContext,
        })

        if (element.innerElements) {
            const nextContext = result === undefined ? undefined : (result as T)
            traverseInnerElementsRecursive(element.innerElements, componentId, visitor, path, depth + 1, nextContext)
        }
    }
}

export function traverseComponents<T = void>(exportData: CoreExportFormat, options: TraverseOptions<T>): void {
    const { onComponent } = options
    const { components, rootComponent } = exportData

    if (!rootComponent || !components?.[rootComponent]) {
        return
    }

    const visited = new Set<string>()

    function walk(id: string, depth: number, parent: ComponentNode | null, parentContext: T | undefined): void {
        if (visited.has(id)) {
            return
        }
        visited.add(id)

        const component = components[id] as ComponentNode
        if (!component) {
            return
        }

        const componentsList = component.components
        const childIds =
            !componentsList || componentsList.length === 0
                ? []
                : componentsList.filter((c): c is string => _.isString(c))

        const result = onComponent?.({
            component,
            id,
            depth,
            parent,
            parentContext,
            childIds,
            traverseInnerElements: visitor => {
                if (component.innerElements) {
                    traverseInnerElements(component.innerElements, id, visitor)
                }
            },
        })

        for (const childId of childIds) {
            walk(childId, depth + 1, component, result)
        }
    }

    walk(rootComponent, 0, null, undefined)
}
